import React from "react";
import { graphql } from "react-apollo";
import { flowRight as compose } from "lodash";
import gql from "graphql-tag";
import {formValueSelector} from "redux-form/immutable";
import {connect} from "react-redux";
import Form from "./EditProductSurchargeForm";
import {GET_PRODUCT_SURCHARGE_FOR_LOCATION} from "../../../queries/sales";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";
import {cancelEditProductSurcharge} from "../../../actions/InventoryApiActions";
import {fromJS, Map, List} from "immutable";
import {validateGreaterThan} from "../../../lib/Validators";
import {createValidationErrors, createErrors} from "../../../lib/ErrorFormatter";
import {isUS, isCanada} from "../../../lib/CountriesAndStates";

function transformSurcharges(values) {
  let surcharge = values.get("surcharge", Map()).reduce((acc, val, key) => {
    return acc.push({
      currency: key,
      value: val
    })
  }, List());
  return values.set("surcharge", surcharge);  
}

function transformStateCode(values) {
  if (isUS(values.get("countryCode"))) return values;
  if (isCanada(values.get("countryCode"))) return values;
  return values.set("stateCode", "");
}

function transformInput(values) {
  let newValues = transformSurcharges(values)
  return transformStateCode(newValues)
}

class EditProductSurchargeFormContainer extends React.Component {

  constructor(props) {
    super(props);
    this.state = { errors: [] }
  }

  handleSubmit(values) {
    // The data has to be translated to be sent out.
    let transValues = transformInput(values)
                          .set("shopId", this.props.shopId)
                          .set("productSku", this.props.productId)
                          .set("countryCode", this.props.countryCode)
                          .set("stateCode", this.props.stateCode);
    return this.props.applyProductSurchargeForLocation(transValues.toJS())
      .then((response) => {
        if (response.data.applyProductSurchargeForLocation.errors.length <= 0) {
          this.props.cancelEditProductSurcharge();
        } else {
          let errors = createValidationErrors(response.data.applyProductSurchargeForLocation.errors);
          this.setState({errors: errors});
        }
      })
      .catch((err) => {
        this.setState({errors: createErrors(err)});
      });      
  }

  handleRemove(values) {
    let data = {
      shopId: this.props.shopId,
      productSku: this.props.productId,
      countryCode: this.props.countryCode,
      stateCode: this.props.stateCode
    }
    return this.props.removeProductSurchargeForLocation(data)
      .then((response) => {
        if (response.data.removeProductSurchargeForLocation.errors.length <= 0) {
          this.props.cancelEditProductSurcharge();
        } else {
          let errors = createValidationErrors(response.data.removeProductSurchargeForLocation.errors);
          this.setState({errors: errors});
        }        
      })
      .catch((err) => {
        this.setState({errors: createErrors(err)});
      });      
  }


  handleCancel() {
    this.props.cancelEditProductSurcharge();
  }

  validateForm(values) {
    let errors = Map();
    let currencyErrors = values.get("surcharge", Map()).reduce((acc, value, currency) => {
      return validateGreaterThan(acc, values.get("surcharge", Map()), currency, 0);
    }, Map());
    // Merge into errors when present.
    if (currencyErrors.size > 0) {
      errors = errors.set("surcharge", currencyErrors);
    }
    return errors.toJS();
  }

  render() {
    let {data} = this.props;
    if (data.loading) return <Loading />
    if (data.error) return <GraphQLErrors error={data.error} />
    let item = fromJS(data.productSurchargeForLocation);

    let initialValues = item.get("surcharge").reduce((acc, x) => {
      return acc.setIn(["surcharge", x.get("currency")], x.get("value"));
    }, fromJS({surcharge: {}}));

    let showStateProvince = false;
    if (isUS(item.get("countryCode"))) showStateProvince = true;
    if (isCanada(item.get("countryCode"))) showStateProvince = true;

    return (
      <Form
        initialValues={initialValues.toJS()}
        surcharge={item.get("surcharge")}
        errors={this.state.errors}
        countryName={item.get("countryName")}
        stateName={item.get("stateName")}
        showStateProvince={showStateProvince}        
        validate={this.validateForm.bind(this)}
        onSubmit={this.handleSubmit.bind(this)}
        onRemove={this.handleRemove.bind(this)}
        onCancel={this.handleCancel.bind(this)}
        />
    )
  }

}


const APPLY_PRODUCT_SURCHARGE = gql`
  mutation applyProductSurchargeForLocation($data: ProductSurchargeInput!) {
    applyProductSurchargeForLocation(input: $data) {
      errors { key message }
    }
  }
`;

const REMOVE_PRODUCT_SURCHARGE = gql`
  mutation removeProductSurchargeForLocation($data: ProductSurchargeForLocationInput!) {
    removeProductSurchargeForLocation(input: $data) {
      errors { key message }
    }
  }
`;


const withProductQuery = compose(
  graphql(GET_PRODUCT_SURCHARGE_FOR_LOCATION, {
    options: (props) => ({
      fetchPolicy: "network-only",
      variables: {
        data: {
          shopId: props.shopId,
          productSku: props.productId,
          countryCode: props.countryCode,
          stateCode: props.stateCode
        }
      }
    })
  }),
  graphql(APPLY_PRODUCT_SURCHARGE, {
    props: ({ mutate }) => ({
      applyProductSurchargeForLocation: (data) => mutate({
        variables: { data: data }
      })
    })
  }),
  graphql(REMOVE_PRODUCT_SURCHARGE, {
    props: ({ mutate }) => ({
      removeProductSurchargeForLocation: (data) => mutate({
        variables: { data: data }
      })
    })
  })  
);

const EditProductSurchargeFormContainerWithData = withProductQuery(EditProductSurchargeFormContainer);

const selector = formValueSelector("ProductSurchargeForm");
function mapStateToProps(state, ownProps) {
  return {
    showUSStates: isUS(selector(state, "countryCode"))
  }
}


export default connect(mapStateToProps, {cancelEditProductSurcharge})(EditProductSurchargeFormContainerWithData);